import * as React from "react";

export interface SegmentedControlOption<T extends string> {
  value: T;
  label: React.ReactNode;
}

export interface SegmentedControlProps<T extends string> {
  /** Names the group for assistive tech; the visual label is intentionally dropped. */
  label: string;
  options: readonly SegmentedControlOption<T>[];
  value: T;
  onChange: (value: T) => void;
}

/** Joined pill buttons for a short, fixed set of choices. */
export function SegmentedControl<T extends string>({
  label,
  options,
  value,
  onChange,
}: SegmentedControlProps<T>) {
  return (
    <div className="cf-seg" role="group" aria-label={label}>
      {options.map((option) => {
        const isOn = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            className={["cf-seg-btn", isOn && "is-on"].filter(Boolean).join(" ")}
            aria-pressed={isOn}
            onClick={() => {
              if (!isOn) onChange(option.value);
            }}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

export interface FilterChipProps {
  pressed: boolean;
  onToggle: (pressed: boolean) => void;
  children: React.ReactNode;
}

/** Single on/off filter. A pressed button, not a checkbox: it acts immediately. */
export function FilterChip({ pressed, onToggle, children }: FilterChipProps) {
  return (
    <button
      type="button"
      className={["cf-chip", pressed && "is-on"].filter(Boolean).join(" ")}
      aria-pressed={pressed}
      onClick={() => onToggle(!pressed)}
    >
      {children}
    </button>
  );
}
